import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Shield, Download, Edit, Trash2, UserX, FileText } from "lucide-react";

const DataRights = () => {
  const [email, setEmail] = useState("");
  const [requestType, setRequestType] = useState("");
  const [message, setMessage] = useState("");
  const { toast } = useToast();

  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();
    
    // For now, just show success message - in production this would send to hello@4fox.uk
    toast({
      title: "Request Submitted",
      description: "We'll process your data rights request within 30 days and contact you at the email provided.",
    });

    setEmail("");
    setRequestType("");
    setMessage("");
  };

  const dataRights = [
    {
      icon: FileText,
      title: "Right to Access",
      description: "Request a copy of all personal data we hold about you",
      action: "access"
    },
    {
      icon: Edit,
      title: "Right to Rectification",
      description: "Request correction of inaccurate or incomplete personal data",
      action: "rectify"
    },
    {
      icon: Trash2,
      title: "Right to Erasure",
      description: "Request deletion of your personal data (Right to be Forgotten)",
      action: "delete"
    },
    {
      icon: UserX,
      title: "Right to Object",
      description: "Object to processing of your personal data for marketing purposes",
      action: "object"
    },
    {
      icon: Download,
      title: "Right to Data Portability",
      description: "Receive your personal data in a machine-readable format",
      action: "export"
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="pt-16">
        <div className="bg-primary py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-white" />
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Your Data Rights
              </h1>
              <p className="text-xl text-white/90">
                Under UK GDPR, you have control over your personal data. Exercise your rights below.
              </p>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4 py-16 max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {dataRights.map((right) => {
              const Icon = right.icon;
              return (
                <Card key={right.action} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{right.title}</CardTitle>
                    <CardDescription>{right.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Submit a Data Rights Request</CardTitle>
              <CardDescription>
                Fill out the form below to exercise any of your data rights. We'll respond within 30 days as required by UK GDPR.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitRequest} className="space-y-6">
                <div>
                  <Label htmlFor="email">Your Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    We'll use this to verify your identity and respond to your request
                  </p>
                </div>

                <div>
                  <Label htmlFor="requestType">Type of Request *</Label>
                  <select
                    id="requestType"
                    value={requestType}
                    onChange={(e) => setRequestType(e.target.value)}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Select a request type...</option>
                    <option value="access">Access - View my data</option>
                    <option value="rectify">Rectification - Correct my data</option>
                    <option value="delete">Erasure - Delete my data</option>
                    <option value="object">Object - Stop marketing</option>
                    <option value="export">Data Portability - Export my data</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message">Additional Details</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Provide any additional information about your request..."
                    rows={4}
                  />
                </div>

                <Button type="submit" className="w-full">
                  Submit Request
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-12 prose prose-lg max-w-none">
            <h2>Response Time</h2>
            <p>
              We will respond to all data rights requests within <strong>30 days</strong> of verification, 
              as required by UK GDPR. In complex cases, we may extend this by an additional 60 days and 
              will inform you if this is necessary.
            </p>

            <h2>Identity Verification</h2>
            <p>
              To protect your privacy, we may need to verify your identity before processing certain requests. 
              This may involve asking for additional information or documentation.
            </p>

            <h2>Contact Us</h2>
            <p>
              For urgent data rights matters or questions about this process, you can also contact us directly at{" "}
              <a href="mailto:hello@4fox.uk" className="text-primary hover:underline">
                hello@4fox.uk
              </a>
            </p>

            <h2>Right to Complain</h2>
            <p>
              If you're not satisfied with how we handle your data rights request, you have the right to 
              lodge a complaint with the Information Commissioner's Office (ICO):
            </p>
            <ul>
              <li>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ico.org.uk</a></li>
              <li>Phone: 0303 123 1113</li>
              <li>Address: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</li>
            </ul>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DataRights;
